import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  makeStyles,
  createStyles,
  Grid,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Theme
} from "@material-ui/core";
import {
  BodyMeasurements,
  BodyMeasurementsForSize,
  BodyMeasurementsKey,
  CalculateClothBodyMeasurementsFromBodyMeasurements,
  CalculateClothingMeasurementsFromBodyMeasurements,
  ClothingMeasurements,
  ClothingType,
  ExtractBodyMeasurementsFromClothBodyMeasurements,
  FitType,
} from "common-ts/dist/models/UserMeasurements";
import { RegularSizeValue, RegularSizeValueShortName } from "common-ts/dist/models/Cart";
import OverlayComponentCanvas from "./OverlayComponentCanvasFitAnimation";
import { FareastButton, FareastButtonGrey } from "./OverlayComponentButton"; // Assuming this path
import { socket } from "../socket";
import { OfflineActiveScreen } from "../redux/UIOverlayOfflineRedux";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      padding: theme.spacing(2),
      overflowY: "auto",
      overflowX: "hidden",
      alignItems: "center",
      justifyContent: "center",
      marginRight: theme.spacing(8),
    },
    contentWrapper: {
      width: "100%",
      maxWidth: "1200px", 
      margin: "0 auto",
      flexGrow: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },
    title: {
      fontSize: "1.8rem",
      fontWeight: 600,
      marginBottom: theme.spacing(1),
      textAlign: "center",
      [theme.breakpoints.down('xs')]: {
         fontSize: "1.5rem",
      }
    },
    subtitle: {
      marginBottom: theme.spacing(4),
      color: "#666",
      fontSize: "1rem",
      textAlign: "center",
      [theme.breakpoints.down('xs')]: {
         marginBottom: theme.spacing(2),
      }
    },
    // Canvas Container Styles matching reference
    canvasContainer: {
      width: '100%',
      height: '100%',
      minHeight: '400px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      [theme.breakpoints.down('xs')]: {
        height: '75vw',
        minHeight: 'auto',
        marginBottom: theme.spacing(2),
      }
    },
    // Button Grid Styles
    buttonWrapper: {
      padding: '0 8px', // Spacing between buttons
    },
    plusSizeHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid #676767',
      paddingBottom: '7px',
      marginRight: '20px',
      marginBottom: '9px',
      marginTop: '20px',
      width: '100%',
    },
    plusBadge: {
      fontSize: '8px',
      padding: '2px 6px 0px',
      marginRight: '7px',
      border: '1px solid rgb(6, 37, 158)',
      borderRadius: '40px',
      color: 'rgb(6, 37, 158)',
      height: 'fit-content',
      fontWeight: 700,
    },
    plusTitle: {
      fontSize: '12px',
      marginRight: '5px',
      paddingTop: '2px',
      fontWeight: 500,
    },
    plusSubtitle: {
      fontSize: '11px',
      fontWeight: 300,
      color: '#232323',
      paddingTop: '2px',
    },
    actionButtonContainer: {
      marginTop: theme.spacing(4),
      display: 'flex',
      justifyContent: 'flex-end',
      width: '100%',
    }
  }),
);

interface Props {
  handleBack: () => void;
  handleSelect: (measurements: BodyMeasurements, size: RegularSizeValue) => void;
  forceChosenSize?: RegularSizeValue;
}

// Helper to extract enum keys
function enumKeys<E>(e: E): (keyof E)[] {
  return Object.keys(e as any) as (keyof E)[];
}

export default function SizePreset({ handleBack, handleSelect, forceChosenSize }: Props) {
  const classes = useStyles();
  const theme = useTheme();
  // Changed to Up 'sm' to match the reference layout logic (Desktop = Grid Side by Side)
  const isMdOrUp = useMediaQuery(theme.breakpoints.up("sm"));

  const [selectedSize, setSelectedSize] = useState<RegularSizeValue | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize with L
  const [goalFit, setGoalFit] = useState<ClothingMeasurements>(() => 
    CalculateClothingMeasurementsFromBodyMeasurements(ClothingType.KemejaPendek, FitType.RegularFit, BodyMeasurementsForSize[RegularSizeValue.L], RegularSizeValue.L)
  );

  // Update on Click
  const onSizeClick = (sizeValue: RegularSizeValue) => {
    setSelectedSize(sizeValue);
    setGoalFit(CalculateClothingMeasurementsFromBodyMeasurements(ClothingType.KemejaPendek, FitType.RegularFit, BodyMeasurementsForSize[sizeValue], RegularSizeValue.L));
    socket.timeout(5000).emit('chat message', {
      currentStore: 'STORE1',
      currentScreen: OfflineActiveScreen.SizePreset,
      justUpdateRegularSizeValue: true,
      currentRegularSizeValue: sizeValue,
    });
  };

  const onConfirm = () => {
    if (!selectedSize || isSubmitting) return;
    setIsSubmitting(true);
    
    // RETURNS CB WITH B-ONLY COMPLETE
    const clothBodyMeasurements = ExtractBodyMeasurementsFromClothBodyMeasurements(CalculateClothBodyMeasurementsFromBodyMeasurements(
      ClothingType.Body,
      FitType.RegularFit,
      BodyMeasurementsForSize[selectedSize], // Use sanitized here
      selectedSize,
      [],
    ));

    handleSelect(clothBodyMeasurements, selectedSize);
  };

  useEffect(() => {
    if (forceChosenSize) {
      setSelectedSize(forceChosenSize);
      setGoalFit(CalculateClothingMeasurementsFromBodyMeasurements(ClothingType.KemejaPendek, FitType.RegularFit, BodyMeasurementsForSize[forceChosenSize], RegularSizeValue.L));
    }
  }, [forceChosenSize]);

  // Helper to render a grid of buttons
  const renderButtons = (keys: string[]) => {
    return (
      <Grid container>
        {keys.map((key) => {
          const value = RegularSizeValue[key as keyof typeof RegularSizeValue];
          const isSelected = selectedSize === value;
          const label = RegularSizeValueShortName[value] || key;

          return (
            <Grid item xs={4} sm={4} key={key}>
              <Box className={classes.buttonWrapper}>
                {isSelected ? (
                   <FareastButton 
                      fullWidth
                      buttonMarginTopBottom="10px"
                      buttonMarginLeftRight="0px"
                      onClick={() => onSizeClick(value)}
                   >
                      {label}
                   </FareastButton>
                ) : (
                   <FareastButtonGrey 
                      fullWidth 
                      buttonMarginTopBottom="10px"
                      buttonMarginLeftRight="0px"
                      onClick={() => onSizeClick(value)}
                   >
                      {label}
                   </FareastButtonGrey>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const allKeys = enumKeys(RegularSizeValue);
  const standardKeys = allKeys.filter(k => 
    k !== "NOTSELECTED" && 
    !["XXXL", "XXXXL", "XXXXXL", "XXXXXXL", "XXXXXXXL", "XXXXXXXXL"].includes(k as string)
  );
  const plusKeys = allKeys.filter(k => 
    ["XXXL", "XXXXL", "XXXXXL", "XXXXXXL", "XXXXXXXL", "XXXXXXXXL"].includes(k as string)
  );

  if (forceChosenSize) {
    return (
      <Box style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
      }}>
        <Typography className={classes.title}>
          Pilih Ukuran Awal Kamu
        </Typography>
        <Typography className={classes.subtitle}>
          Pilih ukuran body yang paling mendekati.
        </Typography>
        <Box className={classes.canvasContainer} style={{height: '100%', width: '100vw'}}>
          <OverlayComponentCanvas
            clothingType={ClothingType.KemejaPendek} 
            fitType={FitType.RegularFit}
            putAtTop={isMdOrUp} // Matches reference: putAtTop={true} for desktop
            scaleRatio={isMdOrUp ? 0.75 : 0.8}
            incrementTogether={true}
            goalFit={goalFit}
          />
        </Box>

        <Box style={{width: '90%'}}>
        {/* Standard Sizes */}
        {renderButtons(standardKeys as string[])}

        {/* Plus Size Section */}
        <Box className={classes.plusSizeHeader}>
          <Box style={{display: 'flex', alignItems: 'center'}}>
              <Typography className={classes.plusBadge}>PLUS</Typography>
              <Typography className={classes.plusTitle}>
                Plus Size
              </Typography>
          </Box>
          <Typography className={classes.plusSubtitle}>
            + Extra Size Charge
          </Typography>
        </Box>

        {/* Plus Sizes */}
        {renderButtons(plusKeys as string[])}

        {/* Submit Button */}
        <Box className={classes.actionButtonContainer}>
            {selectedSize && (
              <FareastButton
                onClick={onConfirm}
                disabled={isSubmitting}
                buttonMarginTopBottom="10px"
                buttonMarginLeftRight="0px"
              >
                {isSubmitting ? <CircularProgress size={20} color="inherit" /> : "PILIH SIZE"}
              </FareastButton>
            )}
        </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.contentWrapper}>
        <Typography className={classes.title}>
          Pilih Ukuran Awal Kamu
        </Typography>
        <Typography className={classes.subtitle}>
          Pilih ukuran body yang paling mendekati.
        </Typography>

        <Grid container spacing={4} direction={isMdOrUp ? "row" : "column-reverse"} style={{ marginTop: 4 }}>
          
          {/* LEFT COLUMN (Desktop): Canvas */}
          <Grid item xs={12} sm={6}>
             <Box className={classes.canvasContainer} style={{height: '100%', width: '40vw', minHeight: '370px'}}>
                <OverlayComponentCanvas
                  clothingType={ClothingType.KemejaPendek} 
                  fitType={FitType.RegularFit}
                  putAtTop={isMdOrUp} // Matches reference: putAtTop={true} for desktop
                  scaleRatio={isMdOrUp ? 0.75 : 0.6}
                  incrementTogether={true}
                  goalFit={goalFit}
                />
             </Box>
          </Grid>

          {/* RIGHT COLUMN (Desktop): Buttons */}
          <Grid item xs={12} sm={6}>
            
            {/* Standard Sizes */}
            {renderButtons(standardKeys as string[])}

            {/* Plus Size Section */}
            <Box className={classes.plusSizeHeader}>
              <Box style={{display: 'flex', alignItems: 'center'}}>
                  <Typography className={classes.plusBadge}>PLUS</Typography>
                  <Typography className={classes.plusTitle}>
                    Plus Size
                  </Typography>
              </Box>
              <Typography className={classes.plusSubtitle}>
                + Extra Size Charge
              </Typography>
            </Box>

            {/* Plus Sizes */}
            {renderButtons(plusKeys as string[])}

            {/* Submit Button */}
            <Box className={classes.actionButtonContainer}>
               {selectedSize && (
                 <FareastButton
                    onClick={onConfirm}
                    disabled={isSubmitting}
                    buttonMarginTopBottom="10px"
                    buttonMarginLeftRight="0px"
                 >
                    {isSubmitting ? <CircularProgress size={20} color="inherit" /> : "PILIH SIZE"}
                 </FareastButton>
               )}
            </Box>

          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}